import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  getVerification,
  listJobVerifications,
  listLessonVerifications,
} from '@platform/client';
import { isTerminal } from '@platform/schema/generation';
import {
  isRead,
  jobVerificationsSchema,
  lessonVerificationsSchema,
  verificationRunSchema,
} from '@platform/schema/verification';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { describeApiError } from '../api-error';
import { jobQuery } from '../library/sources';

import { FindSource, SourceChecks } from './SourceChecks';
import { STAGE } from './stage';

import type {
  CheckedSource,
  Guidance,
  ReadingDepth,
  VerificationRun,
} from '@platform/schema/verification';
import type { ReactNode } from 'react';

/**
 * What a verification run checked and found, as the job page, the Ask AI panel and a
 * lesson's Detailed view show it — CURR-31, on CURR-28's records.
 */

const DEPTH: Record<ReadingDepth, string> = {
  FULL_TEXT: 'Full text',
  PASSAGES: 'Relevant passages',
  ABSTRACT: 'Abstract only',
  METADATA_ONLY: 'Metadata only',
};

const GROUPS: { status: Guidance['status']; title: string }[] = [
  { status: 'APPLIED', title: 'Applied' },
  { status: 'NOT_RELEVANT', title: 'Available but not relevant' },
  { status: 'UNAVAILABLE', title: 'Required but unavailable' },
];

const shortDigest = (digest: string): string =>
  digest.replace(/^sha256:/, '').slice(0, 8);

const when = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

function affected(one: Guidance): string {
  const checks = one.affects ?? [];
  const what =
    checks.length === 0
      ? 'no check reads it yet'
      : `affects ${checks.map((check) => STAGE[check]).join(', ')}`;
  return `${one.blocking ? 'blocks Verified' : 'non-blocking'} · ${what}`;
}

/** Expandable, grouped by what the run did with each piece. */
export function GuidanceSection({
  guidance,
  empty,
}: {
  guidance: readonly Guidance[];
  empty: string;
}) {
  const [open, setOpen] = useState(false);
  const missing = guidance.filter((one) => one.status === 'UNAVAILABLE');

  return (
    <Stack spacing={1} role="region" aria-label="Skills and guidance">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button
          size="small"
          aria-expanded={open}
          onClick={() => {
            setOpen((was) => !was);
          }}
        >
          {open ? 'Hide skills and guidance' : 'Skills and guidance'}
        </Button>
        {guidance.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {GROUPS.map(
              ({ status, title }) =>
                `${String(guidance.filter((one) => one.status === status).length)} ${title.toLowerCase()}`,
            ).join(' · ')}
          </Typography>
        )}
      </Stack>
      {open &&
        (guidance.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {empty}
          </Typography>
        ) : (
          GROUPS.map(({ status, title }) => {
            const items = guidance.filter((one) => one.status === status);
            return (
              <Stack key={status} spacing={0.5}>
                <Typography variant="subtitle2" component="h4">
                  {title}
                </Typography>
                {items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    None.
                  </Typography>
                ) : (
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {items.map((one) => (
                      <Typography component="li" variant="body2" key={one.name}>
                        {one.name}
                        {one.digest === undefined
                          ? ' · no version'
                          : ` · ${shortDigest(one.digest)}`}
                        {status === 'UNAVAILABLE' && ` · ${affected(one)}`}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Stack>
            );
          })
        ))}
      {missing.some((one) => one.blocking) && (
        <Alert severity="error">
          Required guidance was unavailable, so a run it affects cannot read as
          a complete verification.
        </Alert>
      )}
    </Stack>
  );
}

/**
 * The guidance across a job's runs, and each run it filed. Nothing for a job with no
 * verify rows; it asks again only while the job can still file one.
 */
export function JobVerification({
  id,
  runs = true,
}: {
  id: string;
  runs?: boolean;
}) {
  const job = useQuery(jobQuery(id)).data;
  const verifies = job?.rows.some((row) => row.kind === 'VERIFY') === true;
  const found = useQuery({
    queryKey: ['generation', id, 'verifications'],
    enabled: verifies,
    refetchInterval:
      job !== undefined && !isTerminal(job.status) ? 3_000 : (false as const),
    queryFn: async () =>
      jobVerificationsSchema.parse(
        (await listJobVerifications({ path: { id }, throwOnError: true })).data,
      ),
  });
  const [open, setOpen] = useState<string | null>(null);
  if (found.isError)
    return <Alert severity="error">{describeApiError(found.error)}</Alert>;
  const data = found.data;
  if (data === undefined) return null;

  return (
    <Stack spacing={1}>
      <GuidanceSection
        guidance={data.guidance}
        empty="Recorded as each lesson is verified. Nothing in this job has been verified yet."
      />
      {runs && data.runs.length > 0 && (
        <Stack spacing={0.5} role="region" aria-label="Verification runs">
          <Typography variant="subtitle2" component="h3">
            Verification runs
          </Typography>
          {data.runs.map((run) => (
            <Stack
              key={run.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center' }}
            >
              <Typography variant="body2">
                {run.lessonTitle} ·{' '}
                {run.passed ? 'passed every required check' : 'did not pass'}
              </Typography>
              <Button
                size="small"
                aria-expanded={open === run.id}
                onClick={() => {
                  setOpen((was) => (was === run.id ? null : run.id));
                }}
              >
                {open === run.id ? 'Hide the run' : 'Open the run'}
              </Button>
            </Stack>
          ))}
          {open !== null && <RunReport id={open} label="Run" />}
        </Stack>
      )}
    </Stack>
  );
}

/** A built lesson's verification: the latest run in full, and the earlier ones to open. */
export function LessonVerification({ lessonId }: { lessonId: string }) {
  const history = useQuery({
    queryKey: ['verifications', 'lesson', lessonId],
    queryFn: async () =>
      lessonVerificationsSchema.parse(
        (
          await listLessonVerifications({
            path: { id: lessonId },
            throwOnError: true,
          })
        ).data,
      ),
  });
  const [picked, setPicked] = useState<string | null>(null);

  const runs = history.data?.runs ?? [];
  const shown = picked ?? runs[0]?.id ?? null;
  const verified = history.data?.verified;

  return (
    <Stack spacing={1} role="region" aria-label="Verification">
      <Typography variant="subtitle2" component="h3">
        Verification
      </Typography>
      {history.isError && (
        <Alert severity="error">{describeApiError(history.error)}</Alert>
      )}
      {history.data !== undefined &&
        (verified === undefined ? (
          <Chip
            size="small"
            color="warning"
            sx={{ alignSelf: 'flex-start' }}
            label={
              runs.length === 0
                ? 'Not verified: no run yet'
                : runs[0]?.current === false
                  ? 'Not verified: changed since the last run'
                  : 'Not verified: the latest run did not pass'
            }
          />
        ) : (
          <Chip
            size="small"
            color="success"
            sx={{ alignSelf: 'flex-start' }}
            label={`Verified by run ${verified.runId.slice(-8)} · version ${String(verified.lessonVersion)}`}
          />
        ))}
      {shown !== null && (
        <RunReport
          id={shown}
          label={shown === runs[0]?.id ? 'Latest run' : 'Earlier run'}
        />
      )}
      {runs.length > 1 && (
        <Stack spacing={0.5} role="region" aria-label="Earlier runs">
          <Typography variant="subtitle2" component="h4">
            All runs
          </Typography>
          {runs.map((run, index) => (
            <Stack
              key={run.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center' }}
            >
              <Typography variant="body2">
                {when(run.createdAt)} · {run.passed ? 'passed' : 'not passed'}
                {run.current ? '' : ' · older content'}
                {index === 0 ? ' · latest' : ''}
              </Typography>
              <Button
                size="small"
                disabled={run.id === shown}
                onClick={() => {
                  setPicked(run.id);
                }}
              >
                {run.id === shown ? 'Shown' : 'Open'}
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
      {/* The evidence per source; fixing a claim happens in the lesson's review — CURR-38. */}
      <SourceChecks lessonId={lessonId} />
    </Stack>
  );
}

/** One run in full: what it covered, what it skipped, each source, and what it found. */
function RunReport({ id, label }: { id: string; label: string }) {
  const run = useQuery({
    queryKey: ['verifications', 'run', id],
    queryFn: async () =>
      verificationRunSchema.parse(
        (await getVerification({ path: { id }, throwOnError: true })).data,
      ),
  });
  if (run.isError)
    return <Alert severity="error">{describeApiError(run.error)}</Alert>;
  if (run.data === undefined) return null;
  return <Report run={run.data} label={label} />;
}

function Report({ run, label }: { run: VerificationRun; label: string }) {
  const skipped = run.checks.filter((check) => !check.completed);
  const read = run.sources.filter((source) => isRead(source.depth));

  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={1}
      role="region"
      aria-label={`Run ${run.id.slice(-8)}`}
      sx={{ p: 2 }}
    >
      <Typography variant="body2">
        {label} {run.id.slice(-8)} · {when(run.createdAt)} · version{' '}
        {String(run.lessonVersion)} ·{' '}
        {run.passed ? 'passed every required check' : 'did not pass'}
        {run.current ? '' : ' · the lesson has changed since'}
      </Typography>
      {skipped.length > 0 && (
        <Alert severity="error">
          Incomplete: {skipped.length} required check
          {skipped.length === 1 ? ' was' : 's were'} skipped, so this is not a
          complete verification.{' '}
          {skipped
            .map(
              (check) => `${STAGE[check.check]}: ${check.reason ?? 'skipped'}`,
            )
            .join('; ')}
        </Alert>
      )}
      {run.unsupported.length > 0 && (
        <Claims
          lessonId={run.lessonId}
          severity="error"
          title="Unsupported: a source read contradicts or does not state"
          claims={run.unsupported}
        />
      )}
      {run.unresolved.length > 0 && (
        <Claims
          lessonId={run.lessonId}
          severity="warning"
          title="Unresolved: no source read settles"
          claims={run.unresolved}
        />
      )}
      <Facts
        rows={[
          ['Scope reviewed', run.scope.join('; ')],
          [
            'Checks completed',
            `${String(run.checks.length - skipped.length)} of ${String(run.checks.length)}`,
          ],
          [
            'Sources read',
            `${String(read.length)} of ${String(run.sources.length)}`,
          ],
          ['Claims identified', String(run.claims.length)],
        ]}
      />
      <SourceTable sources={run.sources} />
      <Listed title="Limitations" items={run.limitations} />
      <Listed
        title="Findings and proposed fixes"
        items={run.findings.map(
          (one) => `${one.severity}: ${one.finding} Fix: ${one.suggestion}`,
        )}
      />
      {/* A run's findings are its record and carry no id; the lesson's own findings,
          with Fix with AI, are in its review. */}
      {run.findings.length > 0 && (
        <Stack direction="row">
          <Button
            size="small"
            variant="outlined"
            component={RouterLink}
            to={`/authoring/lessons/${run.lessonId}`}
          >
            Fix in review
          </Button>
        </Stack>
      )}
      <Listed title="For a person to check" items={run.manualReview} />
      <GuidanceSection
        guidance={run.guidance}
        empty="This run recorded no guidance."
      />
    </Stack>
  );
}

function SourceTable({ sources }: { sources: readonly CheckedSource[] }) {
  if (sources.length === 0)
    return (
      <Alert severity="warning">This run had no source to check against.</Alert>
    );

  return (
    <Table size="small" aria-label="Sources">
      <TableHead>
        <TableRow>
          <TableCell>Source</TableCell>
          <TableCell>Reached</TableCell>
          <TableCell>Read</TableCell>
          <TableCell>Supports the claim</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sources.map((source, index) => (
          <TableRow key={`${source.title}-${String(index)}`}>
            <TableCell>
              <Typography variant="body2">{source.title}</Typography>
              {source.url !== undefined && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {source.url}
                </Typography>
              )}
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
              >
                {source.type} ·{' '}
                {source.original ? 'Original or official' : 'Secondary'}:{' '}
                {source.originalBasis}
              </Typography>
              {source.replacement !== undefined && (
                <Typography variant="caption" component="div">
                  Replaced by {source.replacement}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Verdict
                ok={source.reached}
                label={source.reached ? 'Reached' : 'Not reached'}
              />
              {!source.reached && source.failure !== undefined && (
                <Typography variant="caption" component="div">
                  {source.failure}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Verdict
                ok={isRead(source.depth)}
                label={isRead(source.depth) ? 'Read' : 'Not read'}
              />
              <Typography variant="caption" component="div">
                {DEPTH[source.depth]}
              </Typography>
              {source.reached && source.failure !== undefined && (
                <Typography variant="caption" component="div">
                  {source.failure}
                </Typography>
              )}
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {source.supported.length} supported ·{' '}
                {source.unsupported.length} unsupported ·{' '}
                {source.unresolved.length} unresolved
              </Typography>
              {source.limitations.length > 0 && (
                <Typography variant="caption" component="div">
                  {source.limitations.join('; ')}
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Verdict({ ok, label }: { ok: boolean; label: string }) {
  return <Chip size="small" color={ok ? 'success' : 'warning'} label={label} />;
}

function Claims({
  lessonId,
  severity,
  title,
  claims,
}: {
  lessonId: string;
  severity: 'error' | 'warning';
  title: string;
  claims: readonly string[];
}) {
  return (
    <Alert severity={severity}>
      <Typography variant="body2">
        {title} ({claims.length})
      </Typography>
      <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
        {claims.map((claim) => (
          <Typography component="li" variant="body2" key={claim}>
            {claim} <FindSource lessonId={lessonId} body={{ claim }} compact />
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}

function Facts({ rows }: { rows: readonly [string, ReactNode][] }) {
  return (
    <Stack spacing={0.5} component="dl" sx={{ m: 0 }}>
      {rows.map(([term, value]) => (
        <Stack key={term} direction="row" spacing={1}>
          <Typography
            component="dt"
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0, width: '40%' }}
          >
            {term}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0 }}>
            {value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function Listed({ title, items }: { title: string; items: readonly string[] }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2" component="h4">
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None.
        </Typography>
      ) : (
        <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((item, index) => (
            <Typography
              component="li"
              variant="body2"
              key={`${String(index)}-${item}`}
            >
              {item}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
